import { money } from "@/lib/api";

export const CLASS_LABEL = { standard: "Standard", premium: "Premium", shared: "Shared" };

const Row = ({ label, value, testId, strong, tone }) => (
  <div className={`flex items-baseline justify-between gap-4 py-1.5 ${strong ? "text-amber-100" : "text-zinc-300"}`}>
    <span className={strong ? "font-serif text-lg" : `text-sm ${tone || "text-zinc-400"}`}>{label}</span>
    <span data-testid={testId} className={strong ? "font-serif text-2xl gold-text font-semibold" : `text-sm tabular-nums ${tone || ""}`}>{value}</span>
  </div>
);

const ChargeRows = ({ fare }) => fare.flat_fee == null ? (
  <>
    <Row label="Base fare" value={money(fare.base_fare)} testId="fare-base" />
    <Row label={`Distance · ${fare.miles} mi × ${money(fare.per_mile)}`} value={money(fare.distance_charge)} testId="fare-distance-charge" />
  </>
) : fare.from_first_mile ? (
  <Row label={`Miles (${fare.extra_miles} × ${money(fare.per_mile)}, from first mile)`} value={money(fare.extra_miles_charge)} testId="fare-extra-miles" />
) : (
  <>
    <Row label={`First ${fare.included_miles} miles`} value={money(fare.flat_fee)} testId="fare-flat-fee" />
    <Row label={`Extra miles (${fare.extra_miles} × ${money(fare.per_mile)})`} value={money(fare.extra_miles_charge)} testId="fare-extra-miles" />
  </>
);

const STATUS_TEXT = { paid: "charged", payment_due: "Payment due", pending: "pending" };

const Extras = ({ wait, cancel }) => (
  <>
    {wait?.amount > 0 && (
      <Row label={`Wait time (${wait.charged_minutes} min × ${money(wait.rate)}) · ${STATUS_TEXT[wait.status] || wait.status}`} value={money(wait.amount)}
        testId="fare-wait-time" tone={wait.status === "payment_due" ? "text-red-300" : undefined} />
    )}
    {wait?.status === "payment_due" && <p data-testid="fare-payment-due" className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">Payment due — the extra charge could not be collected from the saved card.</p>}
    {cancel && (
      <Row label={cancel.fee > 0 ? `Cancel fee (driver ${cancel.covered_pct}% to pickup) · refunded ${money(cancel.refund)}` : `Cancelled · refunded ${money(cancel.refund)}`}
        value={money(cancel.fee)} testId="fare-cancel-fee" />
    )}
  </>
);

export const FareBreakdown = ({ fare, miles, minutes, wait, cancel }) => (
  <div data-testid="fare-breakdown" className="space-y-0.5">
    <div className="mb-3 grid grid-cols-2 gap-3">
      <div className="rounded-lg bg-white/[0.03] p-3">
        <p className="lux-eyebrow">Distance</p>
        <p data-testid="fare-miles" className="mt-1 font-serif text-xl text-amber-50">{miles} mi</p>
      </div>
      <div className="rounded-lg bg-white/[0.03] p-3">
        <p className="lux-eyebrow">Est. time</p>
        <p data-testid="fare-minutes" className="mt-1 font-serif text-xl text-amber-50">{minutes} min</p>
      </div>
    </div>
    <Row label="Vehicle class" value={CLASS_LABEL[fare.vehicle_class || "standard"]} testId="fare-vehicle-class" />
    <ChargeRows fare={fare} />
    <Row label={`Time (${fare.minutes} min × ${money(fare.per_minute)})`} value={money(fare.time_charge)} testId="fare-time-charge" />
    {fare.tolls && <Row label={`Tolls (${fare.tolls.legs} legs × ${money(fare.tolls.per_leg)})`} value={money(fare.tolls.amount)} testId="fare-tolls" />}
    {(fare.discounts || []).map((d) => (
      <Row key={d.kind} label={d.label} value={`−${money(d.amount)}`} testId={`fare-discount-${d.kind}`} tone="text-emerald-300" />
    ))}
    <div className="my-2 border-t border-white/5" />
    <Row label="Subtotal" value={money(fare.subtotal)} testId="fare-subtotal" />
    <Row label={`Tax (${fare.tax_rate}%)`} value={money(fare.tax)} testId="fare-tax" />
    <div className="my-2 border-t border-amber-500/20" />
    <Row label="Total" value={money(fare.total)} testId="fare-total" strong />
    <Extras wait={wait} cancel={cancel} />
  </div>
);
